import { type ExcludeUndefined } from "@validereinc/utilities";
import { isEmpty } from "es-toolkit/compat";
import type {
  EquipmentGetListRequestType,
  EquipmentGetOneRequestType,
  EquipmentType,
  EquipmentTypeType,
} from "../schemas/EquipmentSchemas.ts";
import { EquipmentGetListRequestSchema } from "../schemas/EquipmentSchemas.ts";
import type { ReportWithDownloadV2Type } from "../schemas/ReportSchemas.ts";
import type {
  CreateOneRequestType,
  GetListResponseType,
  GetOneResponseType,
  ResourceAdapterToAdapterQueryKeysType,
  ResourceAdapterType,
  ResponseErrorType,
  ResponseSuccessType,
} from "../util/index.ts";
import { Resources, fetchAndCollate, getFilterObject } from "../util/index.ts";
import type { SavedFiltersAdapterType } from "./index.ts";
import { SavedFiltersAdapter, restAPI } from "./index.ts";

export interface EquipmentAdapterType
  extends Pick<
    ResourceAdapterType<EquipmentType>,
    | "getOne"
    | "getList"
    | "updateOne"
    | "createOne"
    | "deleteOne"
    | "exportList"
  > {
  getOne: (
    params: EquipmentGetOneRequestType
  ) => Promise<GetOneResponseType<EquipmentType>>;
  getList: (
    params: EquipmentGetListRequestType
  ) => Promise<GetListResponseType<EquipmentType>>;
  exportList: (
    params: EquipmentGetListRequestType
  ) => Promise<ReportWithDownloadV2Type>;
  types: {
    getList: ResourceAdapterType<EquipmentTypeType>["getList"];
  };
  savedFilters: SavedFiltersAdapterType<
    typeof EquipmentGetListRequestSchema.shape.filters
  >;
}

export const EquipmentAdapter: EquipmentAdapterType = {
  /**
   * Get a list of equipment
   * @see https://staging-carbon-hub-api.s3.us-west-2.amazonaws.com/openapi/index.html#/equipment/search_equipment
   */
  getList: ({
    page,
    pageSize,
    sortBy,
    sortDirection,
    groupBy,
    filters,
    meta,
  }) => {
    const { status, ...restFilters } = filters ?? {};

    if (meta?.isExcelFile) {
      return restAPI.nodeAPI.POST<GetListResponseType<EquipmentType>>({
        endpoint: "/equipment/search",
        query: meta.period ? { period: meta.period } : undefined,
        body: {
          page,
          page_size: pageSize,
          sort_by: sortBy,
          sort_direction: sortDirection,
          filter: {
            ...getFilterObject(restFilters),
            ...(typeof status === "string" && !isEmpty(status)
              ? { status }
              : getFilterObject<
                  Exclude<EquipmentGetListRequestType["filters"], undefined>
                >({
                  status,
                })),
          },
        },
        ...(meta.isExcelFile
          ? {
              headers: {
                Accept:
                  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
              },
              responseType: "arraybuffer",
            }
          : {}),
      });
    } else {
      return fetchAndCollate(
        ({ page, pageSize }) =>
          restAPI.nodeAPI.POST<GetListResponseType<EquipmentType>>({
            endpoint: "/equipment/search",
            query: meta?.period ? { period: meta.period } : undefined,
            body: {
              page,
              page_size: pageSize,
              sort_by: sortBy,
              sort_direction: sortDirection,
              group_by: groupBy,
              filter: {
                ...getFilterObject(restFilters),
                ...(typeof status === "string" && !isEmpty(status)
                  ? { status }
                  : getFilterObject<
                      ExcludeUndefined<EquipmentGetListRequestType["filters"]>
                    >({
                      status,
                    })),
              },
            },
          }),
        page,
        pageSize
      );
    }
  },

  /**
   * Get a single equipment by id
   * @see https://staging-carbon-hub-api.s3.us-west-2.amazonaws.com/openapi/index.html#/equipment/get_equipment
   */
  getOne: ({ id, meta }) =>
    restAPI.nodeAPI
      .GET<EquipmentType>({
        endpoint: `/equipment/${id}`,
        query: meta?.period ? { period: meta.period } : {},
      })
      .then((resp) => ({
        data: resp,
      })),

  /**
   * Create a single Equipment
   * @see https://staging-carbon-hub-api.s3.us-west-2.amazonaws.com/openapi/index.html#/equipment/create_equipment
   */
  createOne: (params: CreateOneRequestType<EquipmentType>) =>
    restAPI.nodeAPI
      .POST<EquipmentType>({
        endpoint: "/equipment",
        body: params.data,
      })
      .then((resp) => ({ data: resp })),

  /**
   * Update a single Equipment
   * @see https://staging-carbon-hub-api.s3.us-west-2.amazonaws.com/openapi/index.html#/equipment/update_equipment
   */
  updateOne: ({ id, data }: { id: string; data: Partial<EquipmentType> }) =>
    restAPI.nodeAPI
      .PUT<EquipmentType>({
        endpoint: `/equipment/${id}`,
        body: data,
      })
      .then((resp) => ({ data: resp })),

  /**
   * Delete an equipment
   * @see {@link https://staging-carbon-hub-api.s3.us-west-2.amazonaws.com/openapi/index.html#/equipment/delete_equipment}
   */
  deleteOne: ({ id }) =>
    restAPI.nodeAPI.DELETE<ResponseSuccessType | ResponseErrorType>({
      endpoint: `/equipment/${id}`,
    }),

  /**
   * Export a list of equipment by search filter
   * @see https://staging-carbon-hub-api.s3.us-west-2.amazonaws.com/openapi/index.html#/equipment/export_equipment
   */
  exportList: ({ filters, sortBy = "name", sortDirection = "asc", meta }) => {
    const { status, ...restFilters } = filters ?? {};
    return restAPI.nodeAPI.POST<ReportWithDownloadV2Type>({
      endpoint: "/equipment/export",
      query: meta?.period ? { period: meta.period } : undefined,
      body: {
        sort_by: sortBy,
        sort_direction: sortDirection,
        filter: {
          ...getFilterObject(restFilters),
          ...(typeof status === "string" && !isEmpty(status)
            ? { status }
            : getFilterObject<
                ExcludeUndefined<EquipmentGetListRequestType["filters"]>
              >({
                status,
              })),
        },
      },
    });
  },
  /**
   * Equipment Saved Filter APIs
   * @see https://staging-carbon-hub-api.s3.us-west-2.amazonaws.com/openapi/index.html#/equipment/get_equipment_filter
   * @see https://staging-carbon-hub-api.s3.us-west-2.amazonaws.com/openapi/index.html#/equipment/post_equipment_filter
   * @see https://staging-carbon-hub-api.s3.us-west-2.amazonaws.com/openapi/index.html#/equipment/put_equipment_filter
   * @see https://staging-carbon-hub-api.s3.us-west-2.amazonaws.com/openapi/index.html#/equipment/delete_equipment_filter
   */
  savedFilters: SavedFiltersAdapter({
    endpoint: "/equipment/filters",
  }),
  types: {
    /**
     * Get a list of available equipment types
     * @see https://staging-carbon-hub-api.s3.us-west-2.amazonaws.com/openapi/index.html#/equipment/list_equipment_types
     */
    getList: () =>
      // The response to this API call is only an array of types. The response has to be coerced to fit getList response type:
      restAPI.nodeAPI
        .GET<EquipmentTypeType[]>({
          endpoint: "/equipment_types",
        })
        .then((data) => ({
          page_number: 1,
          total_pages: 1,
          page_size: data.length,
          total_entries: data.length,
          data,
        })),
  },
};

export const EquipmentAdapterQueryKeys: ResourceAdapterToAdapterQueryKeysType<EquipmentAdapterType> =
  {
    getOne: (params) => [Resources.EQUIPMENT, "getOne", params.id] as const,
    getList: (params) => [Resources.EQUIPMENT, "getList", params] as const,
    updateOne: (_) => [Resources.EQUIPMENT] as const,
    createOne: (_) => [Resources.EQUIPMENT] as const,
    deleteOne: (_) => [Resources.EQUIPMENT] as const,
    exportList: (params) => [Resources.EQUIPMENT, "export", params] as const,
    types: {
      getList: (params) => [Resources.EQUIPMENT, "types", params] as const,
    },
    savedFilters: {
      getList: (params) =>
        [Resources.EQUIPMENT, "filters", "getList", params] as const,
      updateOne: () => [Resources.EQUIPMENT, "filters"] as const,
      createOne: (_) => [Resources.EQUIPMENT, "filters"] as const,
      deleteOne: (_) => [Resources.EQUIPMENT, "filters"] as const,
    },
  };
